import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  useAlterarRegraDeAprovacao,
  useCandidatosAAprovador,
  useCriarRegraDeAprovacao,
  useRegrasDeAprovacao,
  type RegraDeAprovacao,
} from "../../nucleo/aprovacoes";
import { nomeDaOrganizacao, useOrganizacoesDoAtendimento } from "../../nucleo/horas";
import { ROTULO_DA_URGENCIA, type UrgenciaDoTicket } from "../../nucleo/tickets";
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../ui";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

function quandoAplica(regra: RegraDeAprovacao): string {
  const partes = [
    regra.urgencia ? `urgência ${ROTULO_DA_URGENCIA[regra.urgencia as UrgenciaDoTicket]?.toLocaleLowerCase("pt-BR") ?? regra.urgencia}` : null,
    regra.categoria ? `categoria ${regra.categoria}` : null,
    regra.servico ? `serviço ${regra.servico}` : null,
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(" · ") : "Todos os chamados da empresa";
}

function NovaRegra() {
  const organizacoes = useOrganizacoesDoAtendimento();
  const criar = useCriarRegraDeAprovacao();
  const voltarParaHome = useVoltarParaHome();
  const [organizacao, setOrganizacao] = useState("");
  const candidatos = useCandidatosAAprovador(organizacao);
  const [nome, setNome] = useState("");
  const [urgencia, setUrgencia] = useState("");
  const [categoria, setCategoria] = useState("");
  const [servico, setServico] = useState("");
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [minimo, setMinimo] = useState("1");
  const [pausar, setPausar] = useState(true);
  const [erros, setErros] = useState<Record<string, string>>({});

  function alternar(id: string) {
    setEscolhidos((atuais) => (atuais.includes(id) ? atuais.filter((item) => item !== id) : [...atuais, id]));
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const novos: Record<string, string> = {};
    const quantos = Number(minimo);
    if (!organizacao) novos.organizacao = "Escolha a empresa.";
    if (!nome.trim()) novos.nome = "Dê um nome à regra.";
    if (escolhidos.length === 0) novos.aprovadores = "Escolha ao menos uma pessoa para aprovar.";
    if (!Number.isInteger(quantos) || quantos < 1) novos.minimo = "Informe ao menos 1.";
    else if (quantos > escolhidos.length && escolhidos.length > 0) {
      novos.minimo = "O mínimo não pode passar do número de aprovadores.";
    }
    setErros(novos);
    if (Object.keys(novos).length > 0) return;
    criar.mutate(
      {
        organizacao_id: organizacao,
        nome: nome.trim(),
        urgencia: urgencia || null,
        categoria: categoria.trim() || null,
        servico: servico.trim() || null,
        minimo_aprovacoes: quantos,
        pausar_sla: pausar,
        aprovadores: escolhidos,
      },
      {
        onSuccess: () => {
          setNome("");
          setUrgencia("");
          setCategoria("");
          setServico("");
          setEscolhidos([]);
          setMinimo("1");
          voltarParaHome("Regra de aprovação criada.");
        },
      },
    );
  }

  return (
    <Cartao titulo="Nova regra de aprovação" className="mb-4">
      <p className="nota-rodape mb-3">
        Chamados que caem na regra só seguem depois da decisão dos aprovadores. Deixe urgência, categoria e serviço
        em branco para valer para todos os chamados da empresa. O nome da regra aparece para o cliente no chamado.
      </p>
      <form className="grid gap-3" onSubmit={enviar} data-testid="nova-regra-de-aprovacao" noValidate>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="regra-empresa">Empresa</label>
          <select
            id="regra-empresa"
            className="campo__controle"
            value={organizacao}
            onChange={(evento) => {
              setOrganizacao(evento.target.value);
              setEscolhidos([]);
            }}
          >
            <option value="">Escolha a empresa</option>
            {organizacoes.data?.map((item) => (
              <option value={item.id} key={item.id}>
                {nomeDaOrganizacao(item)}
              </option>
            ))}
          </select>
          {erros.organizacao ? <p className="campo__erro" role="alert">{erros.organizacao}</p> : null}
        </div>
        <Campo rotulo="Nome da regra" value={nome} onChange={(e) => setNome(e.target.value)} erro={erros.nome} maxLength={120} />
        <div className="flex flex-wrap gap-3">
          <label className="campo" htmlFor="regra-urgencia">
            <span className="campo__rotulo">Urgência</span>
            <select id="regra-urgencia" className="campo__controle" value={urgencia} onChange={(e) => setUrgencia(e.target.value)}>
              <option value="">Qualquer</option>
              {Object.entries(ROTULO_DA_URGENCIA).map(([chave, rotulo]) => (
                <option value={chave} key={chave}>{rotulo}</option>
              ))}
            </select>
          </label>
          <Campo rotulo="Categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} maxLength={120} />
          <Campo rotulo="Serviço" value={servico} onChange={(e) => setServico(e.target.value)} maxLength={200} />
        </div>
        <fieldset className="campo">
          <legend className="campo__rotulo">Quem aprova</legend>
          {!organizacao ? (
            <p className="nota-rodape">Escolha a empresa para ver quem pode aprovar.</p>
          ) : candidatos.isPending ? (
            <p role="status">Carregando pessoas…</p>
          ) : candidatos.isError ? (
            <Aviso tom="erro">{mensagemDoConsole(candidatos.error)}</Aviso>
          ) : (
            <ul className="lista-de-itens">
              {candidatos.data.map((pessoa) => (
                <li key={pessoa.id}>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={escolhidos.includes(pessoa.id)}
                      onChange={() => alternar(pessoa.id)}
                    />
                    <span>
                      {pessoa.nome} <span className="nota-rodape">{pessoa.email}{pessoa.interno ? " · equipe" : ""}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {erros.aprovadores ? <p className="campo__erro" role="alert">{erros.aprovadores}</p> : null}
        </fieldset>
        <div className="flex flex-wrap gap-3 items-end">
          <Campo
            rotulo="Aprovações necessárias"
            type="number"
            min={1}
            value={minimo}
            onChange={(e) => setMinimo(e.target.value)}
            erro={erros.minimo}
          />
          <label className="flex items-center gap-2 mb-2">
            <input type="checkbox" checked={pausar} onChange={(e) => setPausar(e.target.checked)} />
            <span>Pausar o prazo do atendimento enquanto aguarda</span>
          </label>
        </div>
        <div>
          <Botao type="submit" disabled={criar.isPending}>
            {criar.isPending ? "Criando…" : "Criar regra"}
          </Botao>
        </div>
        {criar.isError ? <Aviso tom="erro">{mensagemDoConsole(criar.error)}</Aviso> : null}
      </form>
    </Cartao>
  );
}

/** Regras de aprovação condicional: quem aprova o quê, e se o prazo do atendimento para enquanto espera. */
export function PainelDeRegras() {
  const regras = useRegrasDeAprovacao();
  const organizacoes = useOrganizacoesDoAtendimento();
  const alterar = useAlterarRegraDeAprovacao();
  const nomes = new Map((organizacoes.data ?? []).map((item) => [item.id, nomeDaOrganizacao(item)]));

  return (
    <div data-testid="regras-de-aprovacao">
      <NovaRegra />
      {regras.isPending ? (
        <p role="status">Carregando regras…</p>
      ) : regras.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(regras.error)}</Aviso>
      ) : regras.data.length === 0 ? (
        <Aviso tom="marca">Nenhuma regra de aprovação cadastrada.</Aviso>
      ) : (
        <Tabela legenda="Regras de aprovação" data-testid="tabela-de-regras">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Empresa</CelulaCabecalho>
              <CelulaCabecalho>Regra</CelulaCabecalho>
              <CelulaCabecalho>Quando vale</CelulaCabecalho>
              <CelulaCabecalho>Quem aprova</CelulaCabecalho>
              <CelulaCabecalho>Situação</CelulaCabecalho>
              <CelulaCabecalho>Ações</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {regras.data.map((regra) => (
              <Linha key={regra.id} data-testid={`regra-${regra.nome}`}>
                <Celula>{nomes.get(regra.organizacao_id) ?? "—"}</Celula>
                <Celula>{regra.nome}</Celula>
                <Celula>{quandoAplica(regra)}</Celula>
                <Celula>
                  {regra.aprovadores.map((pessoa) => pessoa.nome).join(", ")} · {regra.minimo_aprovacoes} de{" "}
                  {regra.aprovadores.length}
                  {regra.pausar_sla ? " · pausa o prazo" : ""}
                </Celula>
                <Celula>
                  <Chip estado={regra.ativa ? "ok" : "neutro"} semPonto>
                    {regra.ativa ? "Ativa" : "Inativa"}
                  </Chip>
                </Celula>
                <Celula>
                  <Botao
                    variante="fantasma"
                    tamanho="pequeno"
                    aria-label={`${regra.ativa ? "Desativar" : "Ativar"} ${regra.nome}`}
                    disabled={alterar.isPending}
                    onClick={() => alterar.mutate({ id: regra.id, corpo: { ativa: !regra.ativa } })}
                  >
                    {regra.ativa ? "Desativar" : "Ativar"}
                  </Botao>
                </Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
      {alterar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(alterar.error)}</Aviso> : null}
    </div>
  );
}
